import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Loader2, XCircle, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [pollCount, setPollCount] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
        } else if (response.data.status === "expired") {
          setStatus("error");
        } else if (pollCount < 5) {
          // Continue polling
          setTimeout(() => {
            setPollCount(pollCount + 1);
          }, 2000);
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (pollCount < 5) {
          setTimeout(() => {
            setPollCount(pollCount + 1);
          }, 2000);
        } else {
          setStatus("error");
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen bg-[#f4f7fb] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets-jai6qajn.emergentagent.net/job_jambassador-landing/artifacts/91dtn44o_jambassador-standard.png" 
            alt="JamBassador Awards" 
            className="h-20 w-auto mx-auto"
          />
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {status === "checking" && (
            <div className="animate-fade-in">
              <div className="w-20 h-20 bg-[#0b3d91]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-[#0b3d91] animate-spin" />
              </div>
              <h1 className="font-serif text-2xl text-[#132238] mb-2">
                Processing Your Payment...
              </h1>
              <p className="font-sans text-[#4b5563]">
                Please wait while we confirm your payment.
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="animate-fade-in">
              <div className="w-20 h-20 bg-[#2bb673]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#2bb673]" />
              </div>
              <h1 className="font-serif text-3xl text-[#132238] mb-4">
                Thank You for Applying!
              </h1>
              <p className="font-sans text-[#4b5563] mb-6">
                Your application to <strong>JamBassadors 2026</strong> has been successfully submitted.
                Selected candidates will be contacted directly.
              </p>
              <div className="bg-[#e9f8f0] border border-[#2bb673]/30 rounded-xl p-4 mb-6">
                <p className="font-sans text-sm text-[#16643d]">
                  <strong>What's Next?</strong> Our selection committee will review your application. 
                  If selected, you will receive an official invitation to participate.
                </p>
              </div>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0b3d91] text-white font-sans font-semibold rounded-full hover:bg-[#0a3580] transition-colors"
                data-testid="btn-return-home"
              >
                Return Home <ArrowRight size={18} />
              </Link>
            </div>
          )}

          {status === "error" && (
            <div className="animate-fade-in">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="font-serif text-2xl text-[#132238] mb-4">
                Payment Issue
              </h1>
              <p className="font-sans text-[#4b5563] mb-6">
                We couldn't confirm your payment. If you were charged, please contact us at{" "}
                <a href="mailto:Info@imcmedia360.com" className="text-[#0b3d91] hover:underline">
                  Info@imcmedia360.com
                </a>
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/apply"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#f2c94c] text-[#132238] font-sans font-semibold rounded-full hover:bg-[#e5bc3f] transition-colors"
                  data-testid="btn-try-again"
                >
                  Try Again
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 text-[#132238] font-sans font-medium rounded-full hover:bg-gray-50 transition-colors"
                  data-testid="btn-return-home-error"
                >
                  Return Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationSuccess;
